<template>
  <div v-show="message" v-bind="$attrs" ref="aiMessage" class="tw-mb-2 tw-px-4">
    <el-row align="middle" class="tw-flex-nowrap tw-gap-1.5 tw-mb-2">
      <AgentIcon class="tw-mr-2" />

      <AppText color="primary" weight="bold" size="lg">
        {{ t('chat.aiAgent.answer') }}
      </AppText>

      <ChatMessageStatus :shape="status" />

      <AppText size="xs">
        {{ time }}
      </AppText>
    </el-row>

    <el-skeleton
      :loading="isLoadingResources"
      animated
      :count="3"
      data-links-loader
      class="tw-flex tw-gap-2"
    >
      <template #template>
        <el-skeleton-item variant="rect" class="tw-h-40" />
      </template>

      <RecommendationLinks
        v-if="config?.googleLinks.length && isFinished"
        :title="t('chat.aiAgent.recommendationLinks')"
        :links="config.googleLinks"
      />
    </el-skeleton>

    <div ref="container" class="tw-my-2">
      <div v-show="!isAnimated" ref="source" class="markdown-message">
        <span v-html="$sanitize(message)" />
      </div>

      <span v-show="isAnimated" ref="output" class="markdown-message" />
    </div>

    <el-row v-if="isFinished" data-action-button align="middle" class="tw-mb-4">
      <AppButton
        v-if="isPatient && !isRefreshing"
        text
        data-refresh
        @click="refreshMessage"
      >
        <AppIcon
          shape="refresh"
          size="18"
          class="tw-mr-2"
          color="primaryBlack"
        />
        <AppText color="primaryBlack" text-style="uppercase">
          {{ t('chat.aiAgent.refresh') }}
        </AppText>
      </AppButton>
      <AppButton data-copy text @click="handleCopy">
        <AppIcon shape="copy" size="18" class="tw-mr-2" color="primaryBlack" />
        <AppText color="primaryBlack" text-style="uppercase">
          {{ t('chat.aiAgent.copy') }}
        </AppText>
      </AppButton>

      <template v-if="isPatient && !isAiChatEnded">
        <AppButton
          text
          circle
          data-like
          :disabled="feedbackRating === 1"
          only-icon
          :loading="isLoading"
          @click="handleFeedback(1)"
        >
          <AppIcon
            shape="like"
            size="24"
            data-like-icon
            color="primaryBlack"
            :solid="feedbackRating === 1"
          />
        </AppButton>

        <AppButton
          text
          circle
          data-dislike
          :disabled="feedbackRating === -1"
          only-icon
          :loading="isLoading"
          @click="handleFeedback(-1)"
        >
          <AppIcon
            shape="like"
            size="24"
            data-dislike-icon
            direction="down"
            color="primaryBlack"
            :solid="feedbackRating === -1"
          />
        </AppButton>
      </template>
    </el-row>

    <el-skeleton
      :loading="isLoadingResources"
      animated
      :count="3"
      data-youtube-loader
      class="tw-flex tw-gap-2 tw-mb-2"
    >
      <template #template>
        <el-skeleton-item variant="rect" class="tw-h-40" />
      </template>

      <RecommendationLinks
        v-if="config?.youtubeLinks.length && isFinished"
        :title="t('chat.aiAgent.youTubeLinks')"
        :links="config?.youtubeLinks"
        class="tw-mb-2"
      />
    </el-skeleton>

    <el-skeleton
      v-if="isPatient"
      :loading="isLoadingResources"
      data-follow-loader
      animated
    >
      <FollowUpQuestions
        v-if="config?.followUpQuestions.length && isFinished"
        :questions="config?.followUpQuestions"
        :disabled="isAiChatEnded"
      />
    </el-skeleton>
  </div>
</template>

<script setup lang="ts">
/**
 * diagram - https://gitlab.com/qliqdev/qliqweb-vue/-/blob/dev/docs/marmaid/pc/ai-message-rendering.md?ref_type=heads
 * */

import { ElMessage } from 'element-plus'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import ChatMessageStatus from '@shared/components/ChatMessageStatus.vue'
import AgentIcon from '@shared/components/ai-agent/AgentIcon.vue'
import FollowUpQuestions from '@shared/components/chat/gpt/FollowUpQuestions.vue'
import RecommendationLinks from '@shared/components/chat/gpt/RecommendationLinks.vue'
import { MessageStatus } from '@shared/models/enums/messages'
import { AiTypingStatus } from '@shared/models/enums/typing-indicator'
import { IGptConfigDataResponse } from '@shared/models/proto/base/gpt'
import { Nullable } from '@shared/models/types/primary'
import { useTypingIndicatorsStore } from '@shared/store/typing-indicators'
import { useFetchData } from '@shared/utils/composables/fetch-data'
import { Typing } from '@shared/utils/helpers/type-writter-effect'

import { useAiRefreshedMessageScroll } from '@pc/composables/use-ai-refreshed-message-scroll'
import { STICKY_AI_QUESTION_HEIGHT } from '@pc/constants/elements-height'
import { useMessagesStore } from '@pc/store/messages'
import { useSocketStore } from '@pc/store/socket'

const props = withDefaults(
  defineProps<{
    isPatient: boolean
    message: string
    conversationId?: number
    messageId?: number
    messageRating?: number
    time: string
    isAnimated?: boolean
    isLoadingResources?: boolean
    isAiChatEnded?: boolean
    status: MessageStatus
    config?: IGptConfigDataResponse
    gptUuid?: string
  }>(),
  {
    isPatient: false,
    isLoadingResources: false,
    isAiChatEnded: false,
  }
)

const emit = defineEmits<{
  (e: 'scroll'): void
}>()

const messagesStore = useMessagesStore()
const typingStore = useTypingIndicatorsStore()
const socketStore = useSocketStore()
const observer = ref<MutationObserver>()

const { t } = useI18n()

const {
  scrollToReferencedMessage,
  resetRefreshedQuestionMessageId,
  needToAddPadding,
} = useAiRefreshedMessageScroll()

const { fetchData, isLoading } = useFetchData()

const container = ref<HTMLElement>()
const aiMessage = ref<HTMLElement>()
const source = ref<HTMLElement | null>(null)
const output = ref<HTMLElement | null>(null)

const typed = ref<Typing | null>(null)
const feedbackRating = ref<Nullable<number>>(props.messageRating ?? null)
const isFinished = ref(true)

const isRefreshing = computed(() => messagesStore.isRefreshing)

const refreshMessage = () => {
  const { conversationId, messageId } = props

  socketStore.refreshAiMessage({
    conversationId: conversationId ?? 0,
    messageId: messageId ?? 0,
  })

  messageId && scrollToReferencedMessage(messageId)

  isFinished.value = false
}

const handleFeedback = (rating: number) => {
  fetchData({
    onFetchDataCallback: async () => {
      feedbackRating.value = rating
      await messagesStore.setRatings({
        id: props.conversationId ?? 0,
        gptUuid: props.gptUuid ?? '',
        messageId: props.messageId ?? 0,
        rating,
      })

      ElMessage.success(t('pt.feedbackSubmitted'))
    },
    onErrorCatchCallback: () => {
      feedbackRating.value = null
      ElMessage.error(t('pt.feedbackError'))
    },
  })
}

const initTyping = () => {
  if (props?.isAnimated) {
    const messagesToAi = document.querySelectorAll('.message-to-ai')
    const messageHeight = (messagesToAi[messagesToAi.length - 1] as HTMLElement)
      ?.offsetHeight
    const patientHeader = 76
    const padding = 25

    const anchor =
      patientHeader + messageHeight + STICKY_AI_QUESTION_HEIGHT + padding

    observer.value = new MutationObserver(() => {
      const top = aiMessage.value?.getBoundingClientRect().top ?? 0

      // scroll until message question will be in the top of the screen
      if (top > anchor && !isFinished.value) {
        emit('scroll')
      }
    })

    observer.value.observe(aiMessage.value as Node, {
      childList: true,
      subtree: true,
    })

    isFinished.value = false
    needToAddPadding.value = false

    typed.value = new Typing({
      source: source.value,
      output: output.value,
      typeSpeed: 1,
      mode: 'by-word',
      onStart: () => {
        typingStore.setAiIndicatorStatus(AiTypingStatus.RESPONDING)
      },
      onComplete: () => {
        isFinished.value = true
        messagesStore.animateMessage = undefined
        messagesStore.isRefreshing = false

        observer.value?.disconnect()

        resetRefreshedQuestionMessageId()

        typingStore.setAiIndicatorStatus(AiTypingStatus.UNKNOWN)
      },
    })

    typed.value?.start()
  }
}

onMounted(initTyping)

onUnmounted(() => {
  typed.value?.destroy()
})

const handleCopy = () => {
  navigator.clipboard.writeText(source.value?.innerText ?? '')
  ElMessage.success(t('chat.aiAgent.copied'))
}
</script>
